"use client";

import type React from "react";
import { useState, useEffect, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  Container,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  IconButton,
  Fade,
} from "@mui/material";
import {
  BeachAccess,
  PendingActions,
  History,
  Add,
  CheckCircle,
  Warning,
  Info,
  Refresh,
  CalendarMonth,
  WorkHistory,
} from "@mui/icons-material";
import LeaveApplicationTable, {
  type LeaveApplicationItem,
} from "./LeaveApplicationTable";
import LeaveBalanceCard from "./LeaveBalanceCard";
import styles from "./EmployeeLeaveView.module.css";
import { getEmployeeLeave } from "../../api/LeaveService";
import { IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";

const EmployeeLeaveView: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const location = useLocation();
  const [showSuccess, setShowSuccess] = useState(
    location.search.includes("success=true")
  );
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [leaveApplications, setLeaveApplications] = useState<LeaveApplicationItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const TOTAL_ANNUAL_LEAVE_DAYS = 30;

  const fetchLeaveApplications = async () => {
    setLoading(true);
    try {
      const data = await getEmployeeLeave();
      console.log("Leave applications data:", data);
      setLeaveApplications(data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch leave applications:", err);
      setError("Failed to load leave applications. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveApplications();
  }, []);

  useIonViewWillEnter(() => {
    fetchLeaveApplications();
  }, []);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const { usedDays, pendingDays, remainingDays, approvedCount, pendingCount, rejectedCount } = useMemo(() => {
    let used = 0;
    let pending = 0;
    let approved = 0;
    let pendingApps = 0;
    let rejected = 0;

    leaveApplications.forEach((app) => {
      const daysRequested = Number(app.days_requested) || app.daysRequested || 0;
      const status = app.status.toLowerCase();

      if (status === "approved") {
        used += daysRequested;
        approved++;
      } else if (status === "pending") {
        pending += daysRequested;
        pendingApps++;
      } else if (status === "rejected") {
        rejected++;
      }
    });

    const remaining = TOTAL_ANNUAL_LEAVE_DAYS - used;

    return { 
      usedDays: used, 
      pendingDays: pending, 
      remainingDays: remaining,
      approvedCount: approved,
      pendingCount: pendingApps,
      rejectedCount: rejected
    };
  }, [leaveApplications]);

  const getFilteredApplications = () => {
    const today = new Date();

    if (tabValue === 0) {
      return leaveApplications.filter((app) => {
        if (app.status.toLowerCase() === "pending") return true;
        if (app.status.toLowerCase() === "approved") {
          const endDate = new Date(app.end_date || app.endDate || "");
          return endDate >= today;
        }
        return false;
      });
    } else {
      return leaveApplications.filter((app) => {
        if (app.status.toLowerCase() === "rejected") return true;
        if (app.status.toLowerCase() === "approved") {
          const endDate = new Date(app.end_date || app.endDate || "");
          return endDate < today;
        }
        return false;
      });
    }
  };

  const isApplyButtonDisabled = remainingDays <= 0;

  const StatCard = ({ icon: Icon, title, value, color, subtitle }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${theme.palette[color]?.main}15 0%, ${theme.palette[color]?.main}05 100%)`,
        border: `1px solid ${theme.palette[color]?.main}20`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px 0 ${theme.palette[color]?.main}15`,
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: `${theme.palette[color]?.main}20`, color: theme.palette[color]?.main, mr: 2 }}>
            <Icon />
          </Avatar>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette[color]?.main }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <IonPage>
      <IonContent fullscreen>
        <Box sx={{ 
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 100%)`,
          px: { xs: 1, sm: 2 },
          
        }}>
          <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
            {/* Success Alert */}
            <Fade in={showSuccess}>
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.success.main}30`,
                  '& .MuiAlert-message': { width: '100%' }
                }}
                onClose={() => setShowSuccess(false)}
              >
                <AlertTitle sx={{ fontWeight: 600 }}>Success!</AlertTitle>
                Your leave application has been submitted successfully and is under review.
              </Alert>
            </Fade>

            {/* Header Section with Glass Effect */}
            <Card sx={{ 
              mb: 3, 
              background: `linear-gradient(45deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}15 100%)`,
              backdropFilter: 'blur(10px)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: `1px solid ${theme.palette.primary.main}30`,
              borderRadius: 3,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
              minHeight: '120px', // Reduced height
              display: 'flex',
              alignItems: 'center'
            }}>
              <CardContent sx={{ 
                color: 'text.primary', 
                p: { xs: 2, md: 3 }, // Reduced padding
                width: '100%'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  flexWrap: 'wrap', 
                  gap: 2 
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}> {/* Reduced margin */}
                      Leave Management
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8, fontWeight: 400 }}>
                      Manage your time off and track your leave balance
                    </Typography>
                  </Box>
                  <Tooltip
                    title={isApplyButtonDisabled ? "You have no remaining leave days available" : "Apply for new leave"}
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        size="large"
                        component={Link}
                        to={{
                          pathname: "/folder/apply_leavepage",
                          state: { remainingDays },
                        }}
                        disabled={isApplyButtonDisabled}
                        startIcon={<Add />}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: 2,
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          minHeight: '48px',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 20px 0 rgba(0,0,0,0.15)',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'grey.300',
                            color: 'grey.500'
                          }
                        }}
                      >
                        Apply for Leave
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>

            {/* Stats Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={BeachAccess}
                  title="Remaining Leave"
                  value={remainingDays}
                  color="success"
                  subtitle={`of ${TOTAL_ANNUAL_LEAVE_DAYS} days`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={CheckCircle}
                  title="Approved"
                  value={approvedCount}
                  color="info"
                  subtitle="Applications"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={PendingActions}
                  title="Pending"
                  value={pendingCount}
                  color="warning"
                  subtitle="Applications"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={Warning}
                  title="Rejected"
                  value={rejectedCount}
                  color="error"
                  subtitle="Applications"
                />
              </Grid>
            </Grid>

            {/* Main Content */}
            <Grid container spacing={3}>
              {/* Leave Balance Card */}
              <Grid item xs={12} lg={4}>
                <LeaveBalanceCard
                  totalDays={TOTAL_ANNUAL_LEAVE_DAYS}
                  usedDays={usedDays}
                  pendingDays={pendingDays}
                  // className={styles.balanceCard}
                />
              </Grid>

              {/* Applications Section */}
              <Grid item xs={12} lg={8}>
                <Card sx={{ 
                  borderRadius: 3, 
                  boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(5px)'
                }}>
                  <CardContent sx={{ p: 0 }}>
                    {/* Tabs Header */}
                    <Box sx={{ 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      background: `linear-gradient(45deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        px: 3, 
                        pt: 2,
                        pb: 1 // Reduced padding
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}> {/* Smaller heading */}
                          Leave Applications
                        </Typography>
                        <IconButton 
                          onClick={fetchLeaveApplications} 
                          disabled={loading}
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.main', color: 'white' }
                          }}
                        >
                          <Refresh />
                        </IconButton>
                      </Box>
                      <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                          '& .MuiTab-root': {
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            minHeight: 48, // Reduced height
                            '&.Mui-selected': { color: 'primary.main' }
                          }
                        }}
                      >
                        <Tab 
                          icon={<WorkHistory sx={{ fontSize: 18 }} />} // Smaller icon
                          iconPosition="start"
                          label="Active & Pending" 
                        />
                        <Tab 
                          icon={<History sx={{ fontSize: 18 }} />} // Smaller icon
                          iconPosition="start"
                          label="History" 
                        />
                      </Tabs>
                    </Box>

                    {/* Tab Content */}
                    <Box sx={{ p: 2 }}> {/* Reduced padding */}
                      {tabValue === 0 && (
                        <LeaveApplicationTable
                          applications={getFilteredApplications()}
                          loading={loading}
                          error={error}
                          usedDays={usedDays}
                          remainingDays={remainingDays}
                          totalDays={TOTAL_ANNUAL_LEAVE_DAYS}
                        />
                      )}
                      {tabValue === 1 && (
                        <LeaveApplicationTable
                          applications={getFilteredApplications()}
                          loading={loading}
                          error={error}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Quick Actions Footer */}
            {!isMobile && (
              <Paper 
                sx={{ 
                  mt: 4, 
                  p: 3, 
                  borderRadius: 3,
                  background: `linear-gradient(45deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
                  border: `1px solid ${theme.palette.divider}`,
                  backdropFilter: 'blur(5px)',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Tips
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    • Submit leave applications at least 2 weeks in advance for better approval chances
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Check your team's calendar before applying to avoid scheduling conflicts
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • You can cancel pending applications anytime before approval
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Container>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default EmployeeLeaveView;
